<template>
  <div
    class="fixed inset-0 z-[100] flex justify-center overflow-y-auto bg-ink/50 px-4 py-6"
  >
    <div ref="dialogPanel" role="dialog" aria-modal="true" tabindex="-1" @keydown="onDialogKeydown"
      v-if="!showSuccess"
      aria-labelledby="hello-wave-title"
      class="w-full max-w-md flex flex-col bg-sheet rounded-md shadow-ink overflow-hidden my-auto"
    >
      <div class="flex items-center gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
        <p id="hello-wave-title" class="flex-1 min-w-0 text-[15px] font-semibold">{{ $t('helloFeed.wave.title') }}</p>
        <button
          @click="close"
          class="shrink-0 w-11 h-11 -my-1.5 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
          :aria-label="$t('helloFeed.wave.close')"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <div v-if="error" class="mx-4 mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))] shrink-0">
        <CircleAlert class="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <p class="text-[12px] leading-snug">{{ error }}</p>
      </div>

      <div class="overflow-y-auto px-4 py-3.5 space-y-3.5 max-h-[65vh]">
        <p class="text-[12.5px] leading-snug text-[hsl(var(--foreground)/0.8)]">{{ $t('helloFeed.wave.intro') }}</p>

        <div class="flex gap-2">
          <label class="flex-[3] min-w-0 flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('helloFeed.wave.dateLabel') }}</span>
            <input
              v-model="date"
              type="date"
              :max="todayStr"
              class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:transition-opacity [&::-webkit-calendar-picker-indicator]:hover:opacity-90"
            />
          </label>
          <label class="flex-[2] min-w-0 flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('helloFeed.wave.timeLabel') }}</span>
            <input
              v-model="time"
              type="time"
              class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:transition-opacity [&::-webkit-calendar-picker-indicator]:hover:opacity-90"
            />
          </label>
        </div>

        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="consent"
            type="checkbox"
            class="mt-0.5 w-4 h-4 shrink-0 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-[hsl(var(--primary)/0.3)]"
          />
          <span class="text-[12px] leading-snug text-[hsl(var(--foreground)/0.85)]">{{ $t('helloFeed.wave.consent') }}</span>
        </label>
        <button
          type="button"
          class="min-h-11 -my-2 inline-flex items-center text-left text-[11px] text-ink2 underline underline-offset-2"
          @click="showConsent = true"
        >
          {{ $t('helloFeed.wave.consentLink') }}
        </button>
      </div>

      <div class="flex items-center gap-2 px-4 py-3 border-t border-[hsl(var(--border)/0.6)] shrink-0">
        <button
          type="button"
          @click="close"
          class="h-11 px-3 rounded-md bg-[hsl(var(--muted))] text-[12.5px] font-semibold hover:bg-[hsl(var(--muted)/0.8)] transition-colors"
        >{{ $t('helloFeed.wave.cancel') }}</button>
        <button
          type="button"
          @click="submit"
          :disabled="!canSubmit || submitting"
          class="flex-1 h-11 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <Loader2 v-if="submitting" class="w-4 h-4 animate-spin" />
          {{ $t('helloFeed.wave.submit') }}
        </button>
      </div>
    </div>

    <div
      v-else
      role="dialog"
      aria-modal="true"
      aria-labelledby="hello-wave-success-title"
      class="w-full max-w-xs bg-sheet rounded-xl shadow-ink p-5 text-center my-auto"
    >
      <div class="w-11 h-11 mx-auto mb-3 rounded-full bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] flex items-center justify-center">
        <CheckCircle2 class="w-5 h-5" />
      </div>
      <p id="hello-wave-success-title" class="text-[14px] font-semibold mb-1.5">{{ $t('helloFeed.wave.successTitle') }}</p>
      <p class="text-[12.5px] text-ink2 leading-snug mb-4">{{ $t('helloFeed.wave.successMessage') }}</p>
      <button
        type="button"
        @click="dismissSuccess"
        class="w-full h-11 rounded-md bg-[hsl(var(--primary))] text-white text-[12.5px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors"
      >{{ $t('helloFeed.wave.close') }}</button>
    </div>

    <LegalDocumentModal
      v-if="showConsent"
      :document="videoGreetingConsent"
      @close="showConsent = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { X, Loader2, CircleAlert, CheckCircle2 } from "lucide-vue-next";
import api from "../api";
import { videoGreetingConsent } from "../content/legalDocuments";
import { useHistoryBackClose } from "../composables/useHistoryBackClose";
import { useHelloFeedWaveStore } from "../store/helloFeedWave";
import LegalDocumentModal from "./LegalDocumentModal.vue";

const helloFeedWave = useHelloFeedWaveStore();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { t } = useI18n();

const MAX_AGE_DAYS = 14;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}
const now = new Date();
const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

// Префилл текущими локальными датой/временем - обычно посетитель машет и
// тут же открывает форму.
const date = ref(todayStr);
const time = ref(`${pad(now.getHours())}:${pad(now.getMinutes())}`);
const consent = ref(false);

const submitting = ref(false);
const error = ref("");
const showSuccess = ref(false);
const showConsent = ref(false);

const canSubmit = computed(() => Boolean(date.value && time.value && consent.value));

function wavedAtDate(): Date | null {
  if (!date.value || !time.value) return null;
  // `${date}T${time}` без зоны парсится как локальное время браузера -
  // .toISOString() затем даёт корректный UTC.
  const d = new Date(`${date.value}T${time.value}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const submit = async () => {
  if (submitting.value) return;
  error.value = "";
  if (!date.value || !time.value) {
    error.value = t("helloFeed.wave.errorRequired");
    return;
  }
  if (!consent.value) {
    error.value = t("helloFeed.wave.errorConsent");
    return;
  }
  const waved = wavedAtDate();
  if (!waved) {
    error.value = t("helloFeed.wave.errorRequired");
    return;
  }
  const nowMs = Date.now();
  if (waved.getTime() > nowMs + 60 * 60 * 1000) {
    error.value = t("helloFeed.wave.errorFuture");
    return;
  }
  if (waved.getTime() < nowMs - MAX_AGE_DAYS * 24 * 60 * 60 * 1000) {
    error.value = t("helloFeed.wave.errorTooOld");
    return;
  }

  submitting.value = true;
  try {
    await api.post("/hello-feed/wave-requests", {
      waved_at: waved.toISOString(),
      consent: true,
      // Скрытое поле - id ноды, если форма открыта по QR-ссылке /hello/join
      // (см. store/helloFeedWave.ts), иначе null.
      node_id: helloFeedWave.nodeId,
    });
    showSuccess.value = true;
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 400) error.value = t("helloFeed.wave.errorTooOld");
    else error.value = t("helloFeed.wave.errorGeneric");
  } finally {
    submitting.value = false;
  }
};

const close = () => emit("close");

// Кнопка "назад" в браузере закрывает модалку, а не листает страницу под ней.
useHistoryBackClose(close);

const dismissSuccess = () => {
  showSuccess.value = false;
  close();
};

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, { onClose: () => close() });
</script>
